/**
 * Phase 4 plan 04-06 — Create LLM key dialog with save-anyway fork (D-20).
 *
 * Two-step state machine:
 *   step='form'       — collect provider/alias/api_key/is_default, submit.
 *   step='saveAnyway' — on 422, show the provider error and prompt to
 *                       resubmit with save_anyway=true or go back.
 */
import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { AlertTriangle, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  llmApiKeyCreateRequestSchema,
  LLM_PROVIDER_VALUES,
  LLM_PROVIDER_INFO,
  type LLMAPIKeyCreateRequest,
} from '@/schemas/llm-api-key'
import { useOrganizationStore } from '@/stores/organization-store'
import { useLlmApiKeysStore } from '@/stores/llm-api-keys-store'

type Step =
  | { kind: 'form' }
  | { kind: 'saveAnyway'; provider: string; error: string }

interface CreateLlmKeyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateLlmKeyDialog({
  open,
  onOpenChange,
}: CreateLlmKeyDialogProps) {
  const { t } = useTranslation()
  const { currentOrganization } = useOrganizationStore()
  const create = useLlmApiKeysStore((s) => s.create)
  const [step, setStep] = useState<Step>({ kind: 'form' })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { errors },
  } = useForm<LLMAPIKeyCreateRequest>({
    resolver: zodResolver(llmApiKeyCreateRequestSchema),
    defaultValues: {
      name: '',
      provider: 'openai',
      api_key: '',
      is_default: false,
    },
  })

  const handleClose = (value: boolean) => {
    if (isSubmitting) return
    if (!value) {
      reset()
      setStep({ kind: 'form' })
    }
    onOpenChange(value)
  }

  const submitCreate = async (
    data: LLMAPIKeyCreateRequest,
    saveAnyway = false,
  ) => {
    if (!currentOrganization) return
    setIsSubmitting(true)
    try {
      const result = await create(currentOrganization.id, {
        ...data,
        save_anyway: saveAnyway || undefined,
      })
      if (result.ok) {
        toast.success(
          t('settings.llmKeys.createSuccess', { name: result.key.name }),
        )
        reset()
        setStep({ kind: 'form' })
        onOpenChange(false)
      } else if (result.saveAnyway) {
        setStep({
          kind: 'saveAnyway',
          provider: result.provider,
          error: result.error,
        })
      }
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : t('settings.llmKeys.createFailed'),
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const onFormSubmit = handleSubmit((data) => submitCreate(data, false))

  const handleConfirmSaveAnyway = async () => {
    const values = getValues()
    await submitCreate(values, true)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {step.kind === 'form' ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('settings.llmKeys.createTitle')}</DialogTitle>
              <DialogDescription>
                {t('settings.llmKeys.createDescription')}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={onFormSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="llm-key-provider">
                  {t('settings.llmKeys.form.provider')}
                </Label>
                <Controller
                  name="provider"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger id="llm-key-provider">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LLM_PROVIDER_VALUES.map((p) => (
                          <SelectItem key={p} value={p}>
                            {LLM_PROVIDER_INFO[p].label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="llm-key-alias">
                  {t('settings.llmKeys.form.alias')}
                </Label>
                <Input id="llm-key-alias" {...register('name')} />
                {errors.name && (
                  <p className="text-sm text-red-400">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="llm-key-secret">
                  {t('settings.llmKeys.form.apiKey')}
                </Label>
                <Input
                  id="llm-key-secret"
                  type="password"
                  {...register('api_key')}
                />
                {errors.api_key && (
                  <p className="text-sm text-red-400">
                    {errors.api_key.message}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Controller
                  name="is_default"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="llm-key-default"
                      checked={!!field.value}
                      onCheckedChange={(v) => field.onChange(Boolean(v))}
                    />
                  )}
                />
                <Label htmlFor="llm-key-default" className="cursor-pointer">
                  {t('settings.llmKeys.form.isDefault')}
                </Label>
              </div>

              <DialogFooter className="gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleClose(false)}
                  disabled={isSubmitting}
                >
                  {t('settings.llmKeys.form.cancel')}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('settings.llmKeys.validating')}
                    </>
                  ) : (
                    t('settings.llmKeys.form.submit')
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                {t('settings.llmKeys.saveAnyway.title')}
              </DialogTitle>
              <DialogDescription>
                {t('settings.llmKeys.saveAnyway.description')}
              </DialogDescription>
            </DialogHeader>

            <Alert variant="destructive" className="flex items-start gap-3">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-semibold">
                  {t('settings.llmKeys.saveAnyway.providerError', {
                    provider: step.provider,
                  })}
                </p>
                <p className="mt-1 break-words">{step.error}</p>
              </div>
            </Alert>

            <DialogFooter className="gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep({ kind: 'form' })}
                disabled={isSubmitting}
              >
                {t('settings.llmKeys.saveAnyway.cancel')}
              </Button>
              <Button
                type="button"
                onClick={handleConfirmSaveAnyway}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('settings.llmKeys.validating')}
                  </>
                ) : (
                  t('settings.llmKeys.saveAnyway.confirm')
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
